/* SuspensionsList — admin suspension tracker with bulk-unsuspend. */
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

export function SuspensionsList({
  suspensions, isAdmin, selected, setSelected,
  onUnsuspend, onBulkUnsuspend, bulkLimit,
}) {
  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const selectedArr = Array.from(selected);

  if (!suspensions.length) {
    return (
      <Card className="p-8 text-center text-sm text-slate-300" data-testid="emp-suspensions-list-empty">
        No suspensions recorded for this account.
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="emp-suspensions-list">
      {isAdmin && selectedArr.length > 0 && (
        <Card className="flex flex-wrap items-center gap-3 border-amber-400/30 bg-amber-500/10/50 p-3 dark:bg-amber-950/20"
          data-testid="emp-suspensions-bulk-bar">
          <span className="text-sm font-medium" data-testid="emp-suspensions-bulk-count">
            {selectedArr.length} selected
          </span>
          <span className="text-xs text-slate-300">(cap {bulkLimit})</span>
          <Button size="sm" variant="default" className="ml-auto" onClick={onBulkUnsuspend}
            data-testid="emp-suspensions-bulk-unsuspend">Bulk lift</Button>
        </Card>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-300">
            <tr>
              {isAdmin && <th className="w-8 p-2"></th>}
              <th className="p-2">Member</th>
              <th className="p-2">Status</th>
              <th className="p-2">Reason</th>
              <th className="p-2">Issued</th>
              <th className="p-2">Expires</th>
              {isAdmin && <th className="p-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {suspensions.map((s) => {
              const isActive = s.status === 'active';
              return (
                <tr key={s.id} className="border-t border-zinc-200 dark:border-zinc-800"
                  data-testid={`emp-suspensions-row-${s.id}`}>
                  {isAdmin && (
                    <td className="p-2">
                      <Checkbox
                        checked={selected.has(s.id)}
                        onCheckedChange={() => toggle(s.id)}
                        disabled={!isActive}
                        data-testid={`emp-suspensions-select-${s.id}`}
                      />
                    </td>
                  )}
                  <td className="p-2 font-mono text-xs">{s.member_user_id?.slice(0, 8)}…</td>
                  <td className="p-2">
                    <Badge variant={
                      s.status === 'active'  ? 'destructive'
                      : s.status === 'lifted' ? 'secondary'
                      : 'outline'
                    } data-testid={`emp-suspensions-status-${s.id}`}>
                      {s.status}{s.is_emergency ? ' · emergency' : ''}
                    </Badge>
                  </td>
                  <td className="p-2 max-w-xs truncate text-slate-200 dark:text-zinc-300">
                    {s.reason || '—'}
                  </td>
                  <td className="p-2 text-xs text-slate-300">
                    {s.suspended_at ? new Date(s.suspended_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-2 text-xs text-slate-300">
                    {s.expires_at ? new Date(s.expires_at).toLocaleDateString() : 'never'}
                  </td>
                  {isAdmin && (
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" disabled={!isActive}
                        onClick={() => {
                          if (window.confirm('Lift this suspension?')) onUnsuspend(s.id);
                        }}
                        data-testid={`emp-suspensions-unsuspend-${s.id}`}>
                        Lift
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
